import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { Pressable, ScrollView, useColorScheme } from 'react-native';
import { Paragraph, XStack, YStack } from 'tamagui';

import { LoadingIndicator } from '@/components/loading-indicator';
import { PrimaryButton } from '@/components/primary-button';
import { PageEnd } from '@/components/page-end';
import { ScreenContainer } from '@/components/screen-container';
import { useDockClearance, useDockScroll } from '@/lib/dock';
import { SettingsRow, SettingsSection } from '@/components/settings-row';
import { ScreenHeader } from '@/components/screen-header';
import { Sheet } from '@/components/sheet';
import { Subheading } from '@/components/subheading';
import { Text } from '@/components/text';
import { Toggle } from '@/components/toggle';
import { api, type NotificationPreferences } from '@/lib/api';
import { minutesToTime, quietHourOptions } from '@/lib/quiet-hours';
import { queryKeys } from '@/lib/query-keys';
import { darkColors, lightColors, space } from '@/theme/tokens.generated';

function browserTimezone(): string | undefined {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

// Not a SettingsRow: that is a navigation button with a chevron, and
// this row's whole job is the switch at its trailing edge.
function ToggleRow({
  label,
  description,
  value,
  onValueChange,
}: {
  label: string;
  description: string;
  value: boolean;
  onValueChange: (next: boolean) => void;
}) {
  return (
    <XStack minHeight={44} px="$4" py="$3" alignItems="center" justifyContent="space-between" gap="$3">
      <YStack flex={1} gap="$1">
        <Text
          fontFamily="$body"
          color="$color"
          fontSize="$body"
          lineHeight="$body"
          fontWeight="$medium"
          textAlign="left"
        >
          {label}
        </Text>
        <Text fontFamily="$body" color="$colorSecondary" fontSize="$meta" lineHeight="$meta">
          {description}
        </Text>
      </YStack>
      <Toggle value={value} onValueChange={onValueChange} accessibilityLabel={label} />
    </XStack>
  );
}

// A SettingsRow that opens a sheet of half-hour choices.
function TimeRow({ label, minute, onChange }: { label: string; minute: number; onChange: (minute: number) => void }) {
  const [open, setOpen] = useState(false);
  const colorScheme = useColorScheme();
  const colors = colorScheme === 'dark' ? darkColors : lightColors;

  return (
    <>
      <SettingsRow label={label} value={minutesToTime(minute)} onPress={() => setOpen(true)} />
      <Sheet open={open} onOpenChange={setOpen} snapPoints={[60]}>
        <ScrollView accessibilityLabel={`${label} options`}>
          {quietHourOptions().map((option) => (
            <Pressable
              key={option}
              accessibilityRole="button"
              accessibilityState={{ selected: option === minute }}
              aria-pressed={option === minute}
              style={{ minHeight: 44, justifyContent: 'center' }}
              onPress={() => {
                setOpen(false);
                onChange(option);
              }}
            >
              <Text
                fontFamily="$body"
                fontSize="$body"
                lineHeight="$body"
                fontWeight={option === minute ? '700' : '500'}
                color={option === minute ? '$actionPrimary' : '$color'}
                style={
                  option === minute
                    ? { textDecorationLine: 'underline', textDecorationColor: colors.actionPrimary }
                    : undefined
                }
              >
                {minutesToTime(option)}
              </Text>
            </Pressable>
          ))}
        </ScrollView>
      </Sheet>
    </>
  );
}

// Issue #397. Preferences are saved on every change (PUT replaces the
// whole object, so each save sends the full current state); the cache
// is updated first so the switch moves instantly and rolled back if the
// save fails.
export function NotificationSettings() {
  const router = useRouter();
  const dockClearance = useDockClearance();
  const dockScroll = useDockScroll();
  const queryClient = useQueryClient();
  const prefsQuery = useQuery({
    queryKey: queryKeys.notificationPreferences(),
    queryFn: () => api.getNotificationPreferences(),
  });

  const save = useMutation({
    mutationFn: (next: NotificationPreferences) => api.updateNotificationPreferences(next),
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notificationPreferences() });
      const previous = queryClient.getQueryData<NotificationPreferences>(queryKeys.notificationPreferences());

      queryClient.setQueryData(queryKeys.notificationPreferences(), next);

      return { previous };
    },
    onError: (_error, _next, context) => {
      queryClient.setQueryData(queryKeys.notificationPreferences(), context?.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.notificationPreferences() }),
  });

  const prefs = prefsQuery.data;
  const update = (patch: Partial<NotificationPreferences>) => {
    if (prefs) save.mutate({ ...prefs, ...patch });
  };
  // Changing the window re-stamps the zone with the device's, so the
  // hours the user just picked mean their local time.
  const updateQuietHours = (patch: Pick<Partial<NotificationPreferences>, 'quietStartMinute' | 'quietEndMinute'>) =>
    update({ ...patch, timezone: browserTimezone() ?? prefs?.timezone });

  return (
    <ScreenContainer scrollUnderDock>
      <ScrollView
        style={{ flex: 1 }}
        {...dockScroll}
        contentContainerStyle={{ flexGrow: 1, paddingTop: space[4], paddingBottom: dockClearance, gap: space[6] }}
      >
        <ScreenHeader
          title="Notifications"
          subtitle="Choose what can send you a push notification."
          onBack={() => router.back()}
        />

        {prefsQuery.isPending && <LoadingIndicator label="Loading notification settings" />}

        {prefsQuery.isError && (
          <YStack gap="$3" alignItems="flex-start">
            <Paragraph color="$destructive">
              Could not load your notification settings. Check your connection and try again.
            </Paragraph>
            <PrimaryButton onPress={() => prefsQuery.refetch()}>Try again</PrimaryButton>
          </YStack>
        )}

        {prefs && (
          <YStack gap="$3">
            <SettingsSection>
              <ToggleRow
                label="Messages"
                description="New messages in your conversations."
                value={prefs.pushMessages}
                onValueChange={(next) => update({ pushMessages: next })}
              />
              <ToggleRow
                label="Reveals and connections"
                description="Reveal requests and new connections."
                value={prefs.pushConnections}
                onValueChange={(next) => update({ pushConnections: next })}
              />
            </SettingsSection>

            <YStack gap="$2">
              <Subheading>Quiet hours</Subheading>
              <Paragraph color="$colorSecondary">
                Notifications during this window wait until it ends. Set both times the same to turn quiet hours off.
              </Paragraph>
              <SettingsSection>
                <TimeRow
                  label="Start"
                  minute={prefs.quietStartMinute}
                  onChange={(minute) => updateQuietHours({ quietStartMinute: minute })}
                />
                <TimeRow
                  label="End"
                  minute={prefs.quietEndMinute}
                  onChange={(minute) => updateQuietHours({ quietEndMinute: minute })}
                />
              </SettingsSection>
              <Text fontFamily="$body" color="$colorSecondary" fontSize="$meta" lineHeight="$meta">
                Time zone: {prefs.timezone}
              </Text>
            </YStack>

            {save.isError && (
              <Paragraph color="$destructive" fontSize="$meta" lineHeight="$meta">
                Could not save that change. Check your connection and try again.
              </Paragraph>
            )}
            <PageEnd>Changes save as you make them.</PageEnd>
          </YStack>
        )}
      </ScrollView>
    </ScreenContainer>
  );
}
